import { Link } from "@remix-run/react";
import type { BlogPostContent } from "~/utils/seo-blog-content";
import { blogPostTitle } from "~/utils/seo-blog-content";
import { seoPageFontClass } from "~/utils/seo-game-content";
import { getArticleJsonLd, getBreadcrumbListJsonLd, getFaqPageJsonLd } from "~/utils/seo-meta";

const fontClass = seoPageFontClass;

interface BlogPostPageProps {
  content: BlogPostContent;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

export function BlogPostPage({ content }: BlogPostPageProps) {
  const { slug, headline, excerpt, intro, sections, faq, related, internalLinks, category, readTime, datePublished, dateModified, title, description } = content;
  const pagePath = `/blog/${slug}`;

  return (
    <article className="bg-[#ffffff] w-full">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getArticleJsonLd({ headline: title, description, pageUrl: pagePath, datePublished, dateModified }),
          ),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getBreadcrumbListJsonLd([
              { name: "Home", path: "/" },
              { name: "Blog", path: "/blog" },
              { name: headline, path: pagePath },
            ]),
          ),
        }}
      />
      {faq?.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(getFaqPageJsonLd(faq, pagePath)) }}
        />
      )}

      {/* Hero */}
      <div className="seo-section">
        <nav aria-label="Breadcrumb" className="mb-6 text-center">
          <ol className={`${fontClass} inline-flex items-center gap-2 text-[13px] text-[#6a6a6a]`}>
            <li>
              <Link to="/" className="hover:text-[#696cff] transition-colors">Home</Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>
              <Link to="/blog" className="hover:text-[#696cff] transition-colors">Blog</Link>
            </li>
          </ol>
        </nav>
        <div className="seo-hero-head">
          <p className={`${fontClass} text-[#696cff] font-semibold text-[13px] uppercase tracking-wide mb-3`}>
            {category} · {readTime}
          </p>
          <h1 className="seo-h1">{headline}</h1>
          <p className="seo-lead">{excerpt}</p>
          <p className={`${fontClass} text-[#9a9a9a] text-[13px] mt-4`}>
            Published {formatDate(datePublished)}
            {dateModified && dateModified !== datePublished ? ` · Updated ${formatDate(dateModified)}` : ""}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-[760px] mx-auto px-4 md:px-[60px] pb-12 md:pb-16">
        {/* Intro */}
        <div className="seo-body leading-[1.8] space-y-4 mb-10">
          {intro.split(/\n\n+/).map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>

        {/* Sections */}
        {sections.map((section) => (
          <section key={section.heading} className="mt-10">
            <h2 className={`${fontClass} font-bold text-[#000000] text-[24px] md:text-[30px] mb-4 leading-[1.25]`}>
              {section.heading}
            </h2>
            {section.paragraphs?.map((p, j) => (
              <p key={j} className={`${fontClass} text-[#4a4a4a] text-[16px] md:text-[17px] leading-[1.8] mb-4`}>
                {p}
              </p>
            ))}
            {section.bullets && section.bullets.length > 0 && (
              <ul className="mt-3 space-y-3">
                {section.bullets.map((b) => (
                  <li key={b.label} className={`${fontClass} text-[#4a4a4a] text-[16px] md:text-[17px] leading-[1.75]`}>
                    <span className="font-semibold text-[#2c2c2c]">{b.label}:</span> {b.text}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}

        {/* Inline CTA */}
        <div className="mt-12 rounded-2xl border border-[#696cff]/20 bg-[#F6F6FF] px-6 py-6 text-center">
          <p className={`${fontClass} text-[#2c2c2c] text-[16px] md:text-[17px] mb-4`}>
            Prizo turns your Shopify store into a game — spin the wheel, scratch cards and more — to capture emails, recover carts, and drive repeat sales.
          </p>
          <a
            href="https://apps.shopify.com/prizo"
            target="_blank"
            rel="noopener noreferrer"
            className="seo-btn-cta"
          >
            Try Prizo Free on Shopify
          </a>
          {internalLinks && internalLinks.length > 0 && (
            <div className="flex flex-wrap justify-center gap-3 mt-5">
              {internalLinks.map((l) => (
                <Link key={l.href} to={l.href} className="seo-pill">
                  {l.label} →
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* FAQ */}
      {faq?.length > 0 && (
        <div className="seo-band-grey">
          <div className="max-w-[760px] mx-auto px-4 md:px-[60px]">
            <h2 className="seo-h2">Frequently Asked Questions</h2>
            <div className="seo-faq-list">
              {faq.map((item, i) => (
                <div key={i} className="seo-faq-item">
                  <h3 className="seo-h3">{item.question}</h3>
                  <p className="seo-muted">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Related posts */}
      {related?.length > 0 && (
        <div className="seo-section">
          <h2 className="seo-h2">Keep reading</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {related.map((relSlug) => (
              <Link key={relSlug} to={`/blog/${relSlug}`} className="seo-pill">
                {blogPostTitle(relSlug)}
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
